import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { REDIRECT_REQUEST } from 'src/app/app.module';
import { environment } from 'src/environments/environment';
import { PlannedPurpose } from '../models/purpose';
import { GoalsPlanning } from '../models/goals-planning';
import { Mongo } from '../models/mongo';

@Injectable({
  providedIn: 'root',
})
export class BasePlanningService {
  protected http = inject(HttpClient);

  protected readonly BASE_URL = environment.RH_BASE_URL;
  protected readonly planningCollection = 'plannings';

  getAllPlanningOf(tenant: string): Observable<GoalsPlanning[]> {
    return this.http.get<GoalsPlanning[]>(`${this.BASE_URL}/${tenant}/${this.planningCollection}?sort=-year&filter={'$or':[{'draft':false},{'draft': {'$exists':false}}]}`).pipe(
      map((plans) =>
        plans.map((plan) => {
          const { _id, year, _etag, plannedBenefits, completed, locked } = plan;
          return {
            _id,
            _etag,
            year,
            completed,
            locked,
            plannedBenefits: this.mapPlannedPurposes(plannedBenefits),
          };
        })
      )
    );
  }

  getPlanById(tenantId: string, planId: Mongo.ObjectId): Observable<GoalsPlanning> {
    return this.http.get<GoalsPlanning>(`${this.BASE_URL}/${tenantId}/${this.planningCollection}/${planId.$oid}`).pipe(
      map((plan: GoalsPlanning) =>
        Object.assign(
          {},
          {
            ...plan,
            plannedBenefits: this.mapPlannedPurposes(plan.plannedBenefits),
          }
        )
      )
    );
  }

  getById(tenantId: string, planId: string): Observable<GoalsPlanning> {
    return this.http.get<GoalsPlanning>(`${this.BASE_URL}/${tenantId}/${this.planningCollection}/${planId}`).pipe(
      map((plan: GoalsPlanning) =>
        Object.assign(
          {},
          {
            ...plan,
            plannedBenefits: this.mapPlannedPurposes(plan.plannedBenefits),
          }
        )
      )
    );
  }

  findPlanByYear(tenantId: string, year: number, draft: boolean = false): Observable<GoalsPlanning | null> {
    if (draft) {
      return this.http.get<GoalsPlanning[]>(`${this.BASE_URL}/${tenantId}/${this.planningCollection}?filter={'year':${year},'draft':true}&pagesize=1`, { context: new HttpContext().set(REDIRECT_REQUEST, false) }).pipe(map(r => r.length > 0 ? r[0] : null));
    } else {
      return this.http.get<GoalsPlanning[]>(`${this.BASE_URL}/${tenantId}/${this.planningCollection}?filter={'$and':[{'year':${year}},{'$or':[{'draft':false},{'draft': {'$exists':false}}]}]}&pagesize=1`, { context: new HttpContext().set(REDIRECT_REQUEST, false) }).pipe(map(r => r.length === 1 ? r[0] : null));
    }
  }

  updatePlan(tenant: string, planId: Mongo.ObjectId, plan: Partial<GoalsPlanning>): Observable<HttpResponse<any>> {
    return this.http.patch<HttpResponse<any>>(
      `${this.BASE_URL}/${tenant}/${this.planningCollection}/${planId.$oid}`,
      plan,
      {
        observe: 'response',
      }
    );
  }

  mapKpiToQualitativeOrQuantitative(kpis: any[]) {
    return kpis.map((k) => {
      if (k?.type === PlannedPurpose.KpiType.QUALITATIVE) {
        return new PlannedPurpose.QualitativeKpi(k.title, k.description, k.progress);
      }

      return new PlannedPurpose.QuantitativeKpi(k.title, k.description, k.progress, k.target);
    });
  }

  mapPlannedPurposes(plannedPurposes: PlannedPurpose[]): PlannedPurpose[] {
    plannedPurposes.forEach((p, i) => {
      if(!p.actions) {
        Object.assign(p, {actions: []})
      }
      p.actions.forEach((action, j) => {
        if(action.kpis) {
          plannedPurposes[i].actions[j].kpis = this.mapKpiToQualitativeOrQuantitative(action.kpis)
        }
      });
    });
    return plannedPurposes;
  }
}
